var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var Wizard_1;
import React, { Children, cloneElement, Component, Fragment } from 'react';
import isEqual from 'lodash.isequal';
import { StepStage } from '@philips-internal/oswald-ui-constants';
import { AwesomeComponent, ComponentType, Container } from '@philips-internal/oswald-ui-core';
import { EditorContext } from '@philips-internal/oswald-ui-core/lib/component/context/editor-context.js';
import { arrayOf, bool, element, eventListener, expression, fragment, oneOf, oneOfType, record, shape, string } from '@philips-internal/oswald-ui-core/lib/component/prop-types/index.js';
import { AbortableNavigateEvent, AbortableSubmitEvent, NavigateEvent, RenderEvent, SubmitEvent } from '@philips-internal/oswald-ui-core/lib/component/types/index.js';
import { MessageService } from '@philips-internal/oswald-ui-core/lib/services/index.js';
import { StatefulFragment } from '@philips-internal/oswald-ui-engine';
import { classNames, createAbort, enumValues, generateUUID, isDefined, isEmptyObject, isEnumValid, isFunction, isTouchableDevice } from '@philips-internal/oswald-ui-utils';
import { HeadingContext } from '../../heading/components/heading-context/heading-context.js';
import { SuppressHeadingMargin } from '../../heading/suppress-heading-margin/suppress-heading-margin.js';
import { InternalWizardStep } from '../wizard-step/internal-wizard-step.js';
import { WizardStepSlots } from '../wizard-step/wizard-step-constants.js';
import { InternalWizardStepGroup } from '../wizard-step-group/internal-wizard-step-group.js';
import { WizardActions } from './components/wizard-actions/wizard-actions.js';
import { WizardHorizontalHeader } from './components/wizard-horizontal-header/wizard-horizontal-header.js';
import { WizardSidebar } from './components/wizard-sidebar/wizard-sidebar.js';
import { WizardSlideUpPanel } from './components/wizard-slide-up-panel/wizard-slide-up-panel.js';
import WizardAPI from './wizard-api.js';
import { WizardMode } from './wizard-constants.js';
import { WizardContextProvider } from './wizard-context.js';
import { AdjacentStep, createStep, editStep, findAdjacentStepIdentifier, resetStep, submitStep, updateStages } from './wizard-utils.js';
import './wizard.css';
const WizardContainer = {
    acceptedTypes: [
        ComponentType.FORM,
    ],
    name: '',
};
const dynamicStepsPropType = record({
    fragment: fragment,
    identifier: string,
    props: shape({
        done: bool,
        subtitle: expression,
        title: expression,
    }),
});
const dynamicGroupsPropType = record({
    identifier: string,
    props: shape({
        title: expression,
    }),
    steps: dynamicStepsPropType,
});
let Wizard = Wizard_1 = class Wizard extends Component {
    constructor() {
        super(...arguments);
        this.dynamicFragmentsUUID = {};
        this.state = {
            contextGroups: new Map(),
            contextSteps: new Map(),
            current: '',
            panelOpen: false,
            previous: '',
            steps: {},
        };
        this.pushContextualBarCustomClassName = () => {
            if (!this.context) {
                return;
            }
            const actualMode = this.getWizardAcutalMode();
            const className = classNames({
                'c-wizard__contextual-bar': true,
                [`c-wizard--${actualMode}__contextual-bar`]: isEnumValid(WizardMode, actualMode),
            });
            this.popContextualBarCustomClassName = this.context.pushCustomClassName(className);
        };
        this.getDynamicGroupChildren = (group) => {
            if (!group.steps.length) {
                throw Error('[Wizard]: Groups must have at least one step.');
            }
            return group.steps.map((step, index, steps) => this.getDynamicStepMetadata(step, index, steps).element);
        };
        this.updateContextByDynamicData = () => {
            const { dynamic, dynamicGroups, dynamicSteps } = this.props;
            if (!dynamic) {
                return;
            }
            if (!dynamicGroups || !dynamicGroups.length) {
                if (!dynamicSteps || !dynamicSteps.length) {
                    return;
                }
                const contextSteps = new Map();
                for (const dynamicStep of dynamicSteps) {
                    contextSteps.set(dynamicStep.identifier, {
                        ...dynamicStep.props,
                        identifier: dynamicStep.identifier,
                    });
                }
                return this.setState({ contextSteps });
            }
            const contextGroups = new Map();
            for (const dynamicGroup of dynamicGroups) {
                contextGroups.set(dynamicGroup.identifier, {
                    ...dynamicGroup.props,
                    children: this.getDynamicGroupChildren(dynamicGroup),
                    identifier: dynamicGroup.identifier,
                });
            }
            const groupsSteps = [...contextGroups.values()].flatMap(group => group.children);
            const contextSteps = new Map();
            for (const groupStep of groupsSteps) {
                contextSteps.set(groupStep.identifier, {
                    ...groupStep.props,
                    identifier: groupStep.identifier,
                });
            }
            return this.setState({ contextGroups, contextSteps });
        };
        this.addContextStep = (step) => {
            this.setState((prevState) => {
                const { contextSteps: prevContextSteps } = prevState;
                if (prevContextSteps.has(step.identifier)) {
                    return prevState;
                }
                prevContextSteps.set(step.identifier, step);
                return {
                    ...prevState,
                    contextSteps: new Map(prevContextSteps),
                };
            });
        };
        this.removeContextStep = (identifier) => {
            this.setState((prevState) => {
                const { contextSteps: prevContextSteps } = prevState;
                if (!prevContextSteps.has(identifier)) {
                    return prevState;
                }
                prevContextSteps.delete(identifier);
                return {
                    ...prevState,
                    contextSteps: prevContextSteps,
                };
            });
        };
        this.addContextGroup = (group) => {
            this.setState((prevState) => {
                const { contextGroups: prevContextGroups } = prevState;
                if (prevContextGroups.has(group.identifier)) {
                    return prevState;
                }
                prevContextGroups.set(group.identifier, group);
                return {
                    ...prevState,
                    contextGroups: new Map(prevContextGroups),
                };
            });
        };
        this.removeContextGroup = (identifier) => {
            this.setState((prevState) => {
                const { contextGroups: prevContextGroups } = prevState;
                if (!prevContextGroups.has(identifier)) {
                    return prevState;
                }
                prevContextGroups.delete(identifier);
                return {
                    ...prevState,
                    contextGroups: prevContextGroups,
                };
            });
        };
        this.getDynamicStepMetadata = (step, index, steps) => {
            const { fragment, identifier, props } = step;
            const basicProperties = {
                ...props,
                identifier: identifier,
            };
            const result = basicProperties.done
                ? this.getStepResult(identifier)
                : null;
            const properties = createStep(resetStep({}), {
                ...basicProperties,
                collapsed: basicProperties.done ?? true,
                enabled: basicProperties.done ?? false,
                last: index === (steps.length - 1),
                result,
                stage: StepStage.NONE,
            });
            if (!this.dynamicFragmentsUUID[identifier]) {
                this.dynamicFragmentsUUID[identifier] = generateUUID();
            }
            const fragmentElement = Children.toArray((React.createElement(StatefulFragment, { contextData: { identifier }, disposable: true, fragment: fragment, instanceUUID: this.dynamicFragmentsUUID[identifier], 
                // @ts-ignore: Ignoring here because `slot` normally is a property added dynamically through
                // the Oswald editor, and components don't have it in their prop-types.
                slot: WizardStepSlots.CONTENT, uuid: this.dynamicFragmentsUUID[identifier] })));
            const element = (
            // @ts-ignore: Ignoring because all the required properties come from the `properties` variable.
            (React.createElement(InternalWizardStep, { ...properties, key: this.dynamicFragmentsUUID[identifier], uuid: this.dynamicFragmentsUUID[identifier] }, fragmentElement)));
            return {
                element,
                properties,
            };
        };
        this.getFirstNotDoneIdentifier = (steps) => {
            let resultingIdentifier = Object.keys(steps)[0];
            for (const identifier in steps) {
                const data = steps[identifier];
                if (data.done) {
                    continue;
                }
                resultingIdentifier = identifier;
                break;
            }
            return resultingIdentifier;
        };
        this.scrollIntoView = (element, options = {}) => {
            element.scrollIntoView({
                ...options,
                // The behavior has been changed from smooth to auto because there's a bug when using it in combination to Chrome and React[1],
                // where using it prevents the scrollIntoView method from working. Also, Safari does not implement such behavior.
                // [1]: https://github.com/facebook/react/issues/20770
                behavior: 'auto',
            });
        };
        this.scrollToStepStart = () => {
            const element = document.querySelector('.c-wizard-step.c-wizard-step--current');
            if (!element) {
                return;
            }
            return this.scrollIntoView(element);
        };
        this.scrollHeaderToStep = () => {
            const { mode } = this.props;
            const { current } = this.state;
            const shouldScroll = mode === WizardMode.VERTICAL || isTouchableDevice();
            if (!shouldScroll) {
                return;
            }
            const selector = `[data-wizard-step-identifier=${current}]`;
            const element = document.querySelector(selector);
            if (!element) {
                return;
            }
            return this.scrollIntoView(element, {
                block: 'center',
                inline: 'center',
            });
        };
        this.createAfterNavigationCallback = (from, to) => {
            const { onAfterNavigate } = this.props;
            return () => {
                if (isFunction(onAfterNavigate)) {
                    onAfterNavigate(new NavigateEvent({ from, to }));
                }
                this.scrollHeaderToStep();
                this.scrollToStepStart();
            };
        };
        this.onNextStep = (identifier) => {
            const { onNavigate } = this.props;
            const { current, steps } = this.state;
            const abort = createAbort();
            const nextIdentifier = findAdjacentStepIdentifier(AdjacentStep.NEXT, current, steps);
            if (isFunction(onNavigate)) {
                onNavigate(new AbortableNavigateEvent({ abort, from: current, to: nextIdentifier }));
            }
            void abort.promise.then(aborted => {
                if (aborted) {
                    return;
                }
                const submitResult = submitStep(identifier, steps, (identifier) => this.getStepResult(identifier));
                this.setState({
                    current: submitResult.identifier,
                    previous: current,
                    steps: updateStages(submitResult.identifier, submitResult.steps),
                }, this.createAfterNavigationCallback(current, submitResult.identifier));
            });
        };
        this.onPrevStep = () => {
            const { onNavigate } = this.props;
            const { current, steps } = this.state;
            const abort = createAbort();
            const prevIdentifier = findAdjacentStepIdentifier(AdjacentStep.PREVIOUS, current, steps);
            if (isFunction(onNavigate)) {
                onNavigate(new AbortableNavigateEvent({ abort, from: current, to: prevIdentifier }));
            }
            void abort.promise.then(aborted => {
                if (aborted) {
                    return;
                }
                const submitResult = editStep(current, prevIdentifier, steps, (identifier) => this.getStepResult(identifier));
                this.setState({
                    current: submitResult.identifier,
                    previous: current,
                    steps: updateStages(submitResult.identifier, submitResult.steps),
                }, this.createAfterNavigationCallback(current, submitResult.identifier));
            });
        };
        this.onSubmit = () => {
            const { onBeforeSubmit, onSubmit } = this.props;
            const abort = createAbort();
            if (isFunction(onBeforeSubmit)) {
                onBeforeSubmit(new AbortableSubmitEvent({ abort, record: {} }));
            }
            void abort.promise.then((aborted) => {
                if (aborted) {
                    return;
                }
                if (isFunction(onSubmit)) {
                    onSubmit(new SubmitEvent({ record: {} }));
                }
            });
        };
        this.togglePanelOpen = () => {
            this.setState((prevState) => ({ panelOpen: !prevState.panelOpen }));
        };
        this.getClassName = () => {
            const { done } = this.getCurrentStep();
            const actualMode = this.getWizardAcutalMode();
            return classNames({
                'c-wizard': true,
                'c-wizard--current-step-done': done,
                'c-wizard--touchable-device': isTouchableDevice(),
                't-color-signal-green': true,
                [`c-wizard--${actualMode}`]: isEnumValid(WizardMode, actualMode),
                'u-expand': true,
            });
        };
        this.getWrapperClassName = (editorContext) => {
            return classNames({
                'c-wizard__wrapper': true,
                'c-wizard__wrapper--is-designing': Boolean(editorContext?.isDesigning()),
            });
        };
    }
    componentDidMount() {
        const { dynamic } = this.props;
        this.pushContextualBarCustomClassName();
        if (!dynamic) {
            const steps = this.getSteps();
            return this.updateStateSteps(steps);
        }
        this.updateContextByDynamicData();
    }
    componentDidUpdate(previousProps, prevState) {
        const { dynamicGroups, dynamicSteps, hiddenGroups = Wizard_1.defaultProps.hiddenGroups, hiddenSteps = Wizard_1.defaultProps.hiddenSteps, mode, } = this.props;
        const { dynamicGroups: previousDynamicGroups, dynamicSteps: previousDynamicSteps, hiddenGroups: previousHiddenGroups, hiddenSteps: previousHiddenSteps, mode: previousMode, } = previousProps;
        if (mode !== previousMode) {
            if (this.popContextualBarCustomClassName) {
                this.popContextualBarCustomClassName();
            }
            this.pushContextualBarCustomClassName();
        }
        const { contextSteps, contextGroups } = this.state;
        const { contextSteps: previousContextSteps, contextGroups: previousContextGroups } = prevState;
        const dynamicGroupsChanged = !isEqual(dynamicGroups, previousDynamicGroups);
        const dynamicStepsChanged = !isEqual(dynamicSteps, previousDynamicSteps);
        if (dynamicStepsChanged || dynamicGroupsChanged) {
            return this.updateContextByDynamicData();
        }
        const contextStepsChanged = contextSteps.size !== previousContextSteps.size;
        const contextGroupsChanged = contextGroups.size !== previousContextGroups.size;
        const hiddenStepsChanged = !isEqual(hiddenSteps, previousHiddenSteps);
        const hiddenGroupsChanged = !isEqual(hiddenGroups, previousHiddenGroups);
        const shouldUpdateSteps = contextStepsChanged || contextGroupsChanged || hiddenStepsChanged || hiddenGroupsChanged;
        if (shouldUpdateSteps) {
            const steps = this.getSteps();
            this.updateStateSteps(steps);
        }
    }
    componentWillUnmount() {
        if (!this.popContextualBarCustomClassName) {
            return;
        }
        this.popContextualBarCustomClassName();
    }
    getCurrentStep() {
        const { current, steps } = this.state;
        return steps[current] ?? {};
    }
    getStepProperties(step) {
        const { steps } = this.state;
        const { mode, readOnly: wizardReadOnly } = this.props;
        const readOnly = wizardReadOnly ? wizardReadOnly : step.readOnly;
        const stepProperties = steps[step.identifier];
        return {
            ...step,
            ...stepProperties,
            mode,
            readOnly,
        };
    }
    getStepResult(identifier) {
        const { onDynamicContentRender } = this.props;
        if (!isFunction(onDynamicContentRender)) {
            return null;
        }
        const defaultRenderFn = () => null;
        let renderFn = defaultRenderFn;
        const render = (fn) => {
            renderFn = fn;
        };
        onDynamicContentRender(new RenderEvent({
            data: {},
            entry: identifier,
            render,
            type: 'step-result',
        }));
        return renderFn();
    }
    getSteps() {
        const { dynamic, readOnly: wizardReadOnly } = this.props;
        if (dynamic) {
            return this.getDynamicSteps();
        }
        const steps = this.getStepsMetadata();
        const length = steps.length;
        return steps.map((element, index) => {
            const { identifier, readOnly } = element.props || {};
            const properties = createStep(resetStep({}), {
                enabled: false,
                identifier,
                last: index === (length - 1),
                readOnly: wizardReadOnly ? wizardReadOnly : readOnly,
                stage: StepStage.NONE,
            });
            return { element, properties };
        });
    }
    getDynamicSteps() {
        const { dynamicGroups = [], dynamicSteps = [] } = this.props;
        const steps = (dynamicGroups && dynamicGroups.length > 0)
            ? dynamicGroups.flatMap(group => group.steps)
            : dynamicSteps;
        return steps.map(this.getDynamicStepMetadata);
    }
    getStepsMetadata() {
        const { hiddenGroups, hiddenSteps } = this.props;
        const { contextGroups, contextSteps } = this.state;
        const steps = [];
        if (!contextGroups.size) {
            steps.push(...contextSteps.values());
        }
        else {
            for (const [identifier, group] of [...contextGroups.entries()]) {
                const isValidGroup = isDefined(identifier) ? !hiddenGroups.includes(identifier) : true;
                if (!isValidGroup) {
                    continue;
                }
                const childrenArray = Children.toArray(group.children);
                steps.push(...childrenArray.map(({ props }) => props));
            }
        }
        const contextStepsElements = steps.map(stepProps => {
            return (React.createElement(InternalWizardStep, { ...stepProps, key: stepProps.identifier }, stepProps.children));
        });
        return contextStepsElements
            .filter((child) => isDefined(child.props.identifier)
            ? !hiddenSteps.includes(child.props.identifier)
            : true)
            .map((child, index) => ({ ...child, props: { ...child.props, index } }));
    }
    updateStateSteps(steps = []) {
        const state = steps.reduce((accumulator, step) => {
            const { properties } = step;
            return {
                ...accumulator,
                [properties.identifier]: {
                    ...properties,
                },
            };
        }, {});
        const identifier = this.getFirstNotDoneIdentifier(state);
        state[identifier] = createStep(state[identifier], {
            collapsed: false,
            enabled: true,
            identifier,
            stage: StepStage.CURRENT,
        });
        this.setState(prevState => {
            const stepsWithUpdatedStages = updateStages(identifier, state);
            /**
             * Steps should be merged with current state steps because if there is already
             * defined steps on state they could not be up to date due to wizard
             * already executed operations
             */
            const steps = !isEmptyObject(prevState.steps)
                ? { ...stepsWithUpdatedStages, ...prevState.steps }
                : stepsWithUpdatedStages;
            return {
                current: identifier,
                steps,
            };
        });
    }
    renderStep(step, index) {
        const { element, properties } = step;
        return cloneElement(element, {
            ...this.getStepProperties(properties),
            index,
            key: properties.identifier,
        });
    }
    renderStepsContent() {
        const { confirmExpression = Wizard_1.defaultProps.confirmExpression, nextStepExpression = Wizard_1.defaultProps.nextStepExpression, prevStepExpression = Wizard_1.defaultProps.prevStepExpression, readOnly, showNavigationDots, } = this.props;
        const { contextGroups } = this.state;
        const steps = this.getSteps();
        let content = [];
        if (contextGroups.size > 0) {
            for (const group of contextGroups.values()) {
                const groupSteps = steps.filter(step => group.children.some(groupStep => groupStep.props.identifier === step.properties.identifier));
                content.push(React.createElement(InternalWizardStepGroup, { ...group, key: group.identifier }, groupSteps.map((step, index) => this.renderStep(step, index))));
            }
        }
        else {
            content = steps.map((step, index) => this.renderStep(step, index));
        }
        return (React.createElement("div", { className: "c-wizard__content" },
            React.createElement("div", { className: "c-wizard__steps-content u-scrollbar" }, content),
            React.createElement(WizardActions, { confirmExpression: confirmExpression, currentStepMetadataProps: this.getCurrentStep(), mode: this.getWizardAcutalMode(), nextStepExpression: nextStepExpression, onBulletTrailClick: this.togglePanelOpen, onNextStep: this.onNextStep, onPreviousStep: this.onPrevStep, onSubmit: this.onSubmit, prevStepExpression: prevStepExpression, readOnly: readOnly, showTrail: showNavigationDots && isTouchableDevice(), stepsMetadata: steps })));
    }
    renderMobileStepTitle() {
        const { current } = this.state;
        const steps = this.getSteps();
        const currentStep = steps.find(step => step.properties.identifier === current);
        if (!isTouchableDevice() || !currentStep) {
            return null;
        }
        const { element } = currentStep;
        const { title, subtitle } = element.props;
        return (React.createElement("div", { className: "c-wizard__mobile-step-info" },
            React.createElement("div", { className: "c-wizard__mobile-step-title" }, MessageService.translate(title)),
            React.createElement("div", { className: "c-wizard__mobile-step-subtitle" }, MessageService.translate(subtitle ?? ''))));
    }
    renderContent() {
        if (!isTouchableDevice()) {
            return this.renderStepsContent();
        }
        if (!this.popContextualBarCustomClassName) {
            return (React.createElement(Fragment, null,
                this.renderMobileStepTitle(),
                this.renderStepsContent()));
        }
        return (React.createElement(Fragment, null,
            this.renderMobileStepTitle(),
            React.createElement(SuppressHeadingMargin, { uuid: "fake-uuid" }, this.renderStepsContent())));
    }
    renderSidebar() {
        const { readOnly } = this.props;
        const { contextGroups, current, steps } = this.state;
        const mode = this.getWizardAcutalMode();
        if (isTouchableDevice() || mode === WizardMode.HORIZONTAL) {
            return null;
        }
        return (React.createElement(WizardSidebar, { currentStepIdentifier: current, groupsPropsMap: contextGroups, mode: mode, readOnly: readOnly, stepsMetadata: this.getSteps(), stepsMetadataProps: steps }));
    }
    renderSlideUpPanel() {
        const { readOnly } = this.props;
        const { contextGroups, current, panelOpen, steps } = this.state;
        const mode = this.getWizardAcutalMode();
        if (!isTouchableDevice || mode === WizardMode.HORIZONTAL) {
            return null;
        }
        return (React.createElement(WizardSlideUpPanel, { groupsPropsMap: contextGroups, onClose: this.togglePanelOpen, open: panelOpen },
            React.createElement(WizardSidebar, { compact: true, currentStepIdentifier: current, groupsPropsMap: contextGroups, mode: mode, readOnly: readOnly, stepsMetadata: this.getSteps(), stepsMetadataProps: steps })));
    }
    renderHorizontalHeader() {
        const { readOnly } = this.props;
        const { current, steps } = this.state;
        const mode = this.getWizardAcutalMode();
        if (isTouchableDevice() || mode !== WizardMode.HORIZONTAL) {
            return null;
        }
        return (React.createElement(WizardHorizontalHeader, { currentStepIdentifier: current, mode: mode, readOnly: readOnly, stepsMetadata: this.getSteps(), stepsMetadataProps: steps }));
    }
    getWizardAcutalMode() {
        const { mode } = this.props;
        return mode === WizardMode.HORIZONTAL && isTouchableDevice() ? WizardMode.VERTICAL : mode;
    }
    render() {
        const { children, uuid } = this.props;
        const { contextGroups } = this.state;
        const wizardContextValue = {
            addGroup: this.addContextGroup,
            addStep: this.addContextStep,
            removeGroup: this.removeContextGroup,
            removeStep: this.removeContextStep,
        };
        if (contextGroups.size > 0) {
            const steps = this.getSteps();
            const stepsIdentifiers = steps.map(step => step.properties.identifier);
            const groups = [...contextGroups.values()];
            const groupsSteps = groups.flatMap(group => Children.toArray(group.children));
            const groupsStepsIdentifiers = groupsSteps.map(step => step.props.identifier);
            const hasStepWithoutGroup = stepsIdentifiers.some(step => !groupsStepsIdentifiers.includes(step));
            if (hasStepWithoutGroup) {
                throw Error('[Wizard]: Groups and steps in the same level are not supported.');
            }
        }
        return (React.createElement(EditorContext.Consumer, null, editorContext => {
            return (React.createElement("div", { className: this.getWrapperClassName(editorContext) },
                React.createElement(WizardContextProvider, { value: wizardContextValue },
                    React.createElement(Container, { acceptedTypes: WizardContainer.acceptedTypes, className: this.getClassName(), uuid: uuid },
                        React.createElement("div", { className: "c-pre-children" }, children),
                        this.renderSidebar(),
                        this.renderHorizontalHeader(),
                        this.renderContent(),
                        this.renderSlideUpPanel()))));
        }));
    }
};
Wizard.contextType = HeadingContext;
Wizard.propTypes = {
    children: oneOfType([element, arrayOf(element)]).notEnumerable(),
    className: string,
    confirmExpression: expression,
    dynamic: bool.group('dynamic'),
    dynamicGroups: dynamicGroupsPropType.group('dynamic').notEnumerable(),
    dynamicSteps: dynamicStepsPropType.group('dynamic').notEnumerable(),
    hiddenGroups: arrayOf(string).notEnumerable(),
    hiddenSteps: arrayOf(string).notEnumerable(),
    mode: oneOf(enumValues(WizardMode)),
    nextStepExpression: expression,
    onAfterNavigate: eventListener,
    onBeforeSubmit: eventListener,
    onDynamicContentRender: eventListener,
    onNavigate: eventListener,
    onSubmit: eventListener,
    prevStepExpression: expression,
    readOnly: bool,
    showNavigationDots: bool,
    uuid: string,
};
Wizard.defaultProps = {
    confirmExpression: 'tws.wizard.confirm',
    hiddenGroups: [],
    hiddenSteps: [],
    mode: WizardMode.VERTICAL,
    nextStepExpression: 'tws.wizard.next-step',
    prevStepExpression: 'tws.wizard.prev-step',
    readOnly: false,
    showNavigationDots: true,
};
Wizard = Wizard_1 = __decorate([
    AwesomeComponent({ api: WizardAPI, containers: [WizardContainer], type: ComponentType.FORM })
], Wizard);
export { Wizard, };
//# sourceMappingURL=wizard.js.map